/* Keep the multipart submission native so attachments and the provider's CAPTCHA work. */
if (!customElements.get('three-d-submission')) {
  customElements.define('three-d-submission', class extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.form = this.querySelector('[data-submission-form]');
      if (!this.form) return;
      this.details = this.querySelector('[data-submission-details]');
      this.status = this.querySelector('[data-submission-status]');
      this.sizeStatus = this.querySelector('[data-submission-size]');
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.uploadSequence ||= { image: 1, model: 1 };
      this.hasAttempted = false;
      this.querySelectorAll('[data-submission-close]').forEach((button) => { button.hidden = false; });
      this.form.querySelectorAll('[data-submission-file]').forEach((input) => {
        const row = input.closest('.world-submission__upload');
        if (!row || row.closest('[data-submission-added]') || row.querySelector('[data-submission-clear]')) return;
        const clear = document.createElement('button');
        clear.type = 'button';
        clear.className = 'world-submission__add';
        clear.dataset.submissionClear = input.dataset.submissionFile;
        clear.textContent = input.dataset.submissionFile === 'image' ? 'Remove image' : 'Remove file';
        row.append(clear);
      });
      this.form.addEventListener('input', (event) => this.onChange(event), options);
      this.form.addEventListener('change', (event) => this.onChange(event), options);
      this.form.addEventListener('invalid', (event) => {
        this.hasAttempted = true;
        this.revealField(event.target);
        this.validate();
        this.showStatus();
      }, { ...options, capture: true });
      this.form.addEventListener('submit', (event) => {
        this.hasAttempted = true;
        if (this.validate().length) {
          event.preventDefault();
          this.errors.forEach((field) => this.revealField(field));
          this.showStatus();
          this.form.reportValidity();
        }
      }, options);
      this.form.addEventListener('reset', () => {
        // The reset event fires before the browser restores field values.
        queueMicrotask(() => {
          if (!this.isConnected) return;
          this.querySelectorAll('[data-submission-added]').forEach((row) => row.remove());
          this.hasAttempted = false;
          this.refreshUploadButtons();
          this.validate();
          this.showStatus();
        });
      }, options);
      this.addEventListener('click', (event) => this.onClick(event), options);
      window.addEventListener('pageshow', () => {
        this.validate();
        this.showStatus();
      }, options);
      this.refreshUploadButtons();
      this.validate();
      this.showStatus();
    }

    validate() {
      const fields = [...this.form.elements].filter((field) => typeof field.setCustomValidity === 'function');
      fields.forEach((field) => field.setCustomValidity(''));
      for (const field of fields) {
        if (field.disabled) continue;
        if (field.required && !['checkbox', 'radio', 'file', 'select-one', 'select-multiple'].includes(field.type) && !field.value.trim()) {
          field.setCustomValidity('Please complete this field; spaces alone are not enough.');
        }
        if (field.type === 'url' && field.value.trim()) {
          try {
            const url = new URL(field.value.trim());
            if (!['http:', 'https:'].includes(url.protocol) || !url.hostname) throw new Error('Invalid link');
          } catch {
            field.setCustomValidity('Enter a complete link beginning with https:// or http://.');
          }
        }
      }

      const uploads = [...this.form.querySelectorAll('[data-submission-file]')];
      const allowed = {
        image: new Set(['jpg', 'jpeg', 'png', 'webp', 'gif']),
        model: new Set(['stl', 'step', 'stp', 'f3d', 'f3z', 'sldprt', 'sldasm', 'slddrw', '3mf', 'obj', 'iges', 'igs', 'zip']),
      };
      let bytes = 0;
      let count = 0;
      for (const input of uploads) {
        const clear = input.closest('.world-submission__upload')?.querySelector('[data-submission-clear]');
        if (clear) clear.hidden = !input.files.length;
        for (const file of input.files) {
          bytes += file.size;
          count++;
          const extension = file.name.includes('.') ? file.name.split('.').pop().toLowerCase() : '';
          if (!allowed[input.dataset.submissionFile]?.has(extension)) {
            input.setCustomValidity(`“${file.name}” has an unsupported format. Choose one of the formats listed for this field.`);
          } else if (!file.size) {
            input.setCustomValidity(`“${file.name}” is empty. Choose a file with content.`);
          }
        }
      }
      if (bytes > 10000000) {
        uploads.filter((input) => input.files.length).forEach((input) => {
          if (!input.validationMessage) input.setCustomValidity('Attachments exceed 10 MB in total. Remove some files and share a folder link instead.');
        });
      }
      const projectLink = this.form.querySelector('[data-submission-project-link]');
      const hasProjectLink = projectLink?.value.trim() && projectLink.validity.valid;
      if (!hasProjectLink) {
        if (!count && projectLink && !projectLink.validationMessage) {
          projectLink.setCustomValidity('Add a project link, or attach at least one image and one 3D file.');
        } else if (count) {
          for (const kind of ['image', 'model']) {
            const group = uploads.filter((input) => input.dataset.submissionFile === kind);
            const hasUpload = group.some((input) => input.files.length && input.validity.valid);
            if (!hasUpload && group[0] && !group[0].validationMessage) {
              group[0].setCustomValidity(kind === 'image'
                ? 'Add at least one project image, or share a project link above.'
                : 'Add at least one 3D file, or share a project link above.');
            }
          }
        }
      }
      if (this.sizeStatus) {
        this.sizeStatus.textContent = count
          ? `${count} attachment${count === 1 ? '' : 's'} · ${(bytes / 1000000).toFixed(2)} MB of 10 MB`
          : 'No attachments selected · 10 MB total upload limit';
      }
      this.errors = fields.filter((field) => field.willValidate && !field.validity.valid);
      return this.errors;
    }

    revealField(field) {
      for (let details = field.closest('details'); details; details = details.parentElement?.closest('details')) {
        details.open = true;
      }
    }

    showStatus(changedField) {
      for (const field of this.form.elements) {
        if (field.willValidate) {
          field.setAttribute('aria-invalid', String((this.hasAttempted || field === changedField) && !field.validity.valid));
        }
      }
      if (!this.status) return;
      const invalid = this.hasAttempted ? this.errors?.[0] : changedField;
      const message = invalid && !invalid.validity?.valid ? invalid.validationMessage : '';
      this.status.textContent = message ? `Please check your submission: ${message}` : '';
      this.status.hidden = !message;
    }

    onChange(event) {
      this.validate();
      this.showStatus(event.target);
    }

    refreshUploadButtons() {
      this.querySelectorAll('[data-submission-add]').forEach((button) => {
        button.hidden = this.form.querySelectorAll(`[data-submission-file="${button.dataset.submissionAdd}"]`).length >= 10;
      });
    }

    onClick(event) {
      const close = event.target.closest('[data-submission-close]');
      if (close) {
        event.preventDefault();
        if (this.details) {
          this.details.open = false;
          this.details.querySelector('summary')?.focus();
        }
        return;
      }
      const clear = event.target.closest('[data-submission-clear]');
      if (clear) {
        event.preventDefault();
        const input = clear.closest('.world-submission__upload').querySelector('[data-submission-file]');
        input.value = '';
        this.validate();
        this.showStatus();
        input.focus();
        return;
      }
      const add = event.target.closest('[data-submission-add]');
      if (add) {
        event.preventDefault();
        const kind = add.dataset.submissionAdd;
        const group = this.form.querySelectorAll(`[data-submission-file="${kind}"]`);
        if (!group.length || group.length >= 10) return;
        const row = group[0].closest('.world-submission__upload').cloneNode(true);
        row.querySelectorAll('[data-submission-clear]').forEach((button) => button.remove());
        row.dataset.submissionAdded = '';
        const input = row.querySelector('[data-submission-file]');
        const number = ++this.uploadSequence[kind];
        input.name = `${kind}_${number}`;
        input.id = `${group[0].id}-extra-${number}`;
        input.value = '';
        input.setCustomValidity('');
        input.removeAttribute('aria-invalid');
        const label = row.querySelector('label');
        label.htmlFor = input.id;
        label.textContent = `${kind === 'image' ? 'Image' : '3D file'} ${number}`;
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'world-submission__add';
        remove.dataset.submissionRemove = kind;
        remove.textContent = 'Remove';
        remove.setAttribute('aria-label', `Remove ${kind === 'image' ? 'image' : '3D file'} ${number}`);
        row.append(remove);
        add.before(row);
        this.refreshUploadButtons();
        input.focus();
        return;
      }
      const remove = event.target.closest('[data-submission-remove]');
      if (remove) {
        event.preventDefault();
        const kind = remove.dataset.submissionRemove;
        remove.closest('[data-submission-added]')?.remove();
        this.refreshUploadButtons();
        this.validate();
        this.showStatus();
        this.querySelector(`[data-submission-add="${kind}"]`)?.focus();
      }
    }

    disconnectedCallback() {
      this.events?.abort();
    }
  });
}
